// [S-061d / S-251] Index Health — read-only health surface for both
// layers. Direct port of docs/Wireframes/IndexHealth.html. Two columns:
// L1 certified templates on the left, L2 precedent on the right,
// followed by a Health-status strip. Operational actions live in the
// admin portal (S-093) — every CTA here is read-only navigation.
//
// Backed by the real /admin/health/index endpoint (S-250). L1 counts
// come from Firestore template aggregations; L2 + ingestion numbers
// are zero placeholders until DocumentRepositoryPort exposes a list
// method and PrecedentsSearchService stops returning l2Deployed:false
// (tracked under E-INGESTION-UI + E-L2-PRECEDENTS).
import { Link } from "react-router-dom";
import { Alert, Button, LoadingState, PageHead, SectionHead } from "../components";
import {
  useIndexHealth,
  type IndexHealthSnapshot,
  type LifecycleCount,
} from "../data/queries";

// [S-685] The title/eyebrow are static copy that don't depend on the
// fetch, so <PageHead> renders unconditionally — only the content below it
// gates on load/error state. Previously the whole header was replaced by
// <PageLoading> while fetching, so the real title popped in only once data
// resolved (a layout shift none of the other ~20 PageHead screens have).
export function IndexHealth(): JSX.Element {
  const { data, isLoading, isError, refetch, isFetching } = useIndexHealth();
  const showLoading = isLoading || data === undefined;

  return (
    <>
      <PageHead
        eyebrow="Operations · search status"
        title={
          <>
            Both sources, <em>at a glance.</em>
          </>
        }
        sub={
          showLoading
            ? "Fetching health snapshot."
            : isError
              ? "The read API responded with an error. Try again in a moment."
              : "Read-only health of the certified templates and your precedent index. Operational actions live in the admin portal."
        }
        cta={
          !showLoading && !isError ? (
            <>
              <span className="ih__refresh">
                {`Generated ${formatAgo(new Date(data.generatedAt))}`}
              </span>
              <Button
                variant="secondary"
                className="btn--s"
                onClick={() => {
                  void refetch();
                }}
                disabled={isFetching}
              >
                {isFetching ? "Refreshing…" : "Refresh"}
              </Button>
            </>
          ) : undefined
        }
      />

      {showLoading && <LoadingState label="Loading search status…" />}

      {!showLoading && isError && (
        <Alert label="Health snapshot unavailable" tone="error">
          The read API responded with an error. Try again in a moment.
        </Alert>
      )}

      {!showLoading && !isError && (
        <>
          <div className="home__split">
            <CertifiedLibraryColumn snapshot={data} />
            <StudioPrecedentColumn snapshot={data} />
          </div>

          <HealthStrip snapshot={data} />
        </>
      )}
    </>
  );
}

function CertifiedLibraryColumn({
  snapshot,
}: {
  snapshot: IndexHealthSnapshot;
}): JSX.Element {
  const cl = snapshot.certifiedLibrary;
  return (
    <div className="ih__col" data-testid="ih-col-cert">
      <SectionHead
        title={
          <>
            <span className="page__head__eyebrow" style={{ marginRight: 8 }}>
              <span className="src-dot cert" aria-hidden="true" />
              Layer 1
            </span>
            Templates
          </>
        }
      />

      <div className="ih__kpis">
        <div className="kpi kpi--cert">
          <div className="kpi__lbl">Templates</div>
          <div className="kpi__num">{formatInt(cl.totalTemplates)}</div>
          <div className="kpi__sub">across 6 guilds</div>
        </div>
        <div className="kpi kpi--cert">
          <div className="kpi__lbl">Certified &amp; current</div>
          <div className="kpi__num">{formatInt(cl.certifiedAndCurrent)}</div>
          <div className="kpi__sub">
            in <code>certified</code> + <code>re_certified</code> states
          </div>
        </div>
        <div className="kpi kpi--cert">
          <div className="kpi__lbl">Awaiting re-attestation</div>
          <div className="kpi__num">{formatInt(cl.awaitingReattestation)}</div>
          <div className="kpi__sub">
            in <code>under_review</code> →{" "}
            <Link to="/reattestation">View schedule</Link>
          </div>
        </div>
      </div>

      <LifecycleCard
        title="Lifecycle breakdown"
        total={`Total · ${formatInt(cl.totalTemplates)}`}
        rows={cl.lifecycle}
        layer="cert"
        footer={
          <>
            <span>Last sync: {formatOptionalTimestamp(cl.lastSyncAt)}</span>
            <span>{formatOptionalAgo(cl.lastSyncAt)}</span>
          </>
        }
      />

      {cl.awaitingReattestation > 0 && (
        <div className="alert">
          <div className="alert__lbl">Heads up</div>
          <div className="alert__txt">
            <b>{cl.awaitingReattestation} templates</b> flagged
            <code> under_review</code>.
          </div>
          <Link className="alert__cta" to="/reattestation">
            View schedule →
          </Link>
        </div>
      )}
    </div>
  );
}

function StudioPrecedentColumn({
  snapshot,
}: {
  snapshot: IndexHealthSnapshot;
}): JSX.Element {
  const sp = snapshot.studioPrecedent;
  return (
    <div className="ih__col" data-testid="ih-col-stud">
      <SectionHead
        title={
          <>
            <span className="page__head__eyebrow" style={{ marginRight: 8 }}>
              <span className="src-dot stud" aria-hidden="true" />
              Layer 2
            </span>
            Precedent
          </>
        }
      />

      <div className="ih__kpis">
        <div className="kpi kpi--stud">
          <div className="kpi__lbl">Documents indexed</div>
          <div className="kpi__num">{formatInt(sp.documentsIndexed)}</div>
          <div className="kpi__sub">across {formatInt(sp.deals)} deals</div>
        </div>
        <div className="kpi kpi--stud">
          <div className="kpi__lbl">Ingested last 24h</div>
          <div className="kpi__num">{formatInt(sp.ingested24h)}</div>
          <div className="kpi__sub">
            since {formatOptionalTimestamp(sp.lastIndexedAt)}
          </div>
        </div>
        <div className="kpi kpi--stud">
          <div className="kpi__lbl">DLQ depth</div>
          <div className="kpi__num">{formatInt(sp.dlqDepth)}</div>
          <div className="kpi__sub">
            failed messages awaiting replay
          </div>
        </div>
      </div>

      <LifecycleCard
        title="Lifecycle breakdown"
        total="DocumentLifecycleState"
        rows={sp.lifecycle}
        layer="stud"
        footer={
          <>
            <span>
              Last indexed: {formatOptionalTimestamp(sp.lastIndexedAt)} ·{" "}
              {formatOptionalAgo(sp.lastIndexedAt)}
            </span>
            <span
              className={`ih__pill ${sp.dlqDepth === 0 ? "is-ok" : "is-fail"}`}
              style={{ padding: "2px 8px", fontSize: "10.5px" }}
            >
              <span className="dot" />
              DLQ: {sp.dlqDepth}
            </span>
          </>
        }
      />

      {(() => {
        const failedCount =
          sp.lifecycle.find((r) => r.state === "failed")?.count ?? 0;
        if (failedCount === 0) return null;
        return (
          <div className="alert ih__alert--red">
            <div className="alert__lbl">Action needed</div>
            <div className="alert__txt">
              <b>{failedCount} documents</b> failed ingestion in the last
              24h.
            </div>
            <a className="alert__cta" href="/admin/portal">
              Open admin portal →
            </a>
          </div>
        );
      })()}
    </div>
  );
}

function LifecycleCard({
  title,
  total,
  rows,
  layer,
  footer,
}: {
  title: string;
  total: string;
  rows: LifecycleCount[];
  layer: "cert" | "stud";
  footer: JSX.Element;
}): JSX.Element {
  const sum = rows.reduce((a, r) => a + r.count, 0);
  return (
    <div className="ih__lc" data-testid={`ih-lc-${layer}`}>
      <div className="ih__lc__lbl">
        <span>{title}</span>
        <span className="total">{total}</span>
      </div>
      {rows.map((r) => {
        const pct = sum === 0 ? 0 : (r.count / sum) * 100;
        const isMuted = r.state === "deprecated";
        const isFail = r.state === "failed";
        const isIndexed = r.state === "indexed";
        const fillClass = isIndexed
          ? "ok"
          : layer === "cert"
            ? "cert"
            : "stud";
        return (
          <div
            key={r.state}
            className={`ih__lc__row${isMuted ? " is-muted" : ""}${isFail ? " is-fail" : ""}`}
          >
            <span className="name">
              {r.state === "stage3_human" ? (
                <Link to="/admin/queue">{r.state}</Link>
              ) : (
                r.state
              )}
            </span>
            <span className="ing__bar">
              <span
                className={`ing__bar__fill ${fillClass}`}
                style={{ width: `${Math.max(pct, 0.05)}%` }}
              />
            </span>
            <span className="num">{formatInt(r.count)}</span>
          </div>
        );
      })}
      <div className="ih__lc__foot">{footer}</div>
    </div>
  );
}

function HealthStrip({ snapshot }: { snapshot: IndexHealthSnapshot }): JSX.Element {
  const h = snapshot.health;
  return (
    <div className="ih__strip">
      <div className="ih__strip__lbl">Health status</div>
      <div className="ih__pills">
        <span className={`ih__pill ${pillClass(h.readApi)}`}>
          <span className="dot" />
          <span className="lbl">
            <b>Read API:</b> {h.readApi}
          </span>
        </span>
        <span className={`ih__pill ${pillClass(h.layer2Client)}`}>
          <span className="dot" />
          <span className="lbl">
            <b>Layer 2 client:</b> {h.layer2Client}
          </span>
        </span>
        <span
          className={`ih__pill ${
            h.lastAuditEmitSecondsAgo === null ? "is-warn" : "is-ok"
          }`}
        >
          <span className="dot" />
          <span className="lbl">
            <b>Last audit emit:</b>{" "}
            {h.lastAuditEmitSecondsAgo === null
              ? "unknown"
              : `${h.lastAuditEmitSecondsAgo}s ago`}
          </span>
        </span>
      </div>

      <div className="ih__strip__states">
        <div className="ih__strip__states__lbl">States (reference)</div>
        <div className="ih__pills">
          <span className="ih__pill is-ok">
            <span className="dot" />
            <span className="lbl">connected</span>
          </span>
          <span className="ih__pill is-warn">
            <span className="dot" />
            <span className="lbl">degraded</span>
          </span>
          <span className="ih__pill is-fail">
            <span className="dot" />
            <span className="lbl">disconnected</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function pillClass(s: "connected" | "degraded" | "disconnected"): string {
  if (s === "connected") return "is-ok";
  if (s === "degraded") return "is-warn";
  return "is-fail";
}

function formatInt(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    hour12: false,
    timeZoneName: "short",
  });
}

function formatAgo(d: Date): string {
  const diffMs = Date.now() - d.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function formatOptionalTimestamp(iso: string | null): string {
  return iso === null ? "—" : formatTimestamp(iso);
}

function formatOptionalAgo(iso: string | null): string {
  return iso === null ? "—" : formatAgo(new Date(iso));
}
